import React, { FC, useEffect, useState } from 'react'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import FhirLoader from '../../FhirLoader'
import { useFormik } from 'formik'
import { appConfig } from '../../../assets/customizations/config'
import { Link } from 'react-router-dom'
import InputMask from 'react-input-mask'
import { isBefore, sub } from 'date-fns'
import { MemberSchema } from '../../../utils/validation'
import { renderFormikError } from '../../../utils/helpers'

type MemberProps = {
  initialValues: any
  memberType: string
  validateResults: any
  isLoading: boolean
  isError: boolean
  error: string
  onSubmit: (values: any) => void,
  validatedMember: any
}

const Member: FC<MemberProps> = ({ initialValues, validateResults, isLoading, isError, error, memberType, onSubmit, validatedMember }) => {
  const [showError, setShowError] = useState(false)
  const [showDuplicateError, setShowDuplicateError] = useState(false)

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      memberType,
    },
    validationSchema: MemberSchema,
    onSubmit: (values) => {
      onSubmit({
        memberId: values.memberId,
        dob: values.dob,
        ssn: values.ssn,
        lastname: values.memberLastName,
      })
    },
    enableReinitialize: true
  })

  useEffect(() => {
    validatedMember.reset()
    setShowError(false)
    setShowDuplicateError(false)
  }, [])

  useEffect(() => {
    if (validateResults) {
      if (validateResults.verified === false) {
        setShowError(true)
      }

      if (validateResults.duplicateAccount) {
        setShowDuplicateError(true)
      }
    }

    if (isError) {
      setShowError(true)
    }
  }, [isError, validateResults])

  return (
    <Form noValidate onSubmit={formik.handleSubmit} className='formContainer'>
      <h4>Member Information</h4>
      {showError && (
        <Alert variant='danger' dismissible onClose={() => setShowError(false)}>
          {error ? (
            <div dangerouslySetInnerHTML={{ __html: error }} />
          ) : 'We were not able to verify this member in our system based on the information provided. If you need to locate your Member ID, please call one of the support numbers'
          }
        </Alert>
      )}
      {showDuplicateError && (
        <Alert variant='danger' dismissible onClose={() => setShowDuplicateError(false)}>
          Account already exists. You can try resetting your password or can <Link to='/contact'>contact us</Link> with any questions or concerns.
        </Alert>
      )}
      <Form.Group>
        <Form.Label htmlFor='memberId'>Member ID:</Form.Label>
        <Form.Control
          id='memberId'
          type='text'
          name='memberId'
          value={formik.values.memberId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          isInvalid={!!formik.errors.memberId && !!formik.touched.memberId}
          aria-invalid={!!formik.errors.memberId && !!formik.touched.memberId}
          aria-describedby={formik.errors.memberId && formik.touched.memberId ? 'memberId-error' : ''}
          aria-required="true"
        />
        <Form.Control.Feedback id="memberId-error" type="invalid" role="alert">
          {renderFormikError(formik.errors.memberId)}
        </Form.Control.Feedback>
      </Form.Group>
      {appConfig.use_member_last_name_flag && (
        <Form.Group>
          <Form.Label htmlFor='memberLastName'>Member Last Name:</Form.Label>
          <Form.Control
            id='memberLastName'
            type='text'
            name='memberLastName'
            value={formik.values.memberLastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isInvalid={!!formik.errors.memberLastName && !!formik.touched.memberLastName}
            aria-invalid={!!formik.errors.memberLastName && !!formik.touched.memberLastName}
            aria-describedby={
              formik.errors.memberLastName && formik.touched.memberLastName
                ? 'memberLastName-error'
                : ''
            }
            aria-required="true"
          />
          <Form.Control.Feedback id="memberLastName-error" type="invalid" role="alert">
            {renderFormikError(formik.errors.memberLastName)}
          </Form.Control.Feedback>
        </Form.Group>
      )}
      <Form.Group className='mt-3'>
        {appConfig.use_dob_flag && (
          <>
            <Form.Label htmlFor='dob'>Member Date of Birth:</Form.Label>
            <InputMask
              mask="99/99/9999"
              value={formik.values.dob}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {(inputProps) => (
                <Form.Control
                  id='dob'
                  {...inputProps}
                  name="dob"
                  placeholder="MM/DD/YYYY"
                  isInvalid={!!formik.errors.dob && !!formik.touched.dob}
                  aria-invalid={!!formik.errors.dob && !!formik.touched.dob}
                  aria-describedby={
                    formik.errors.dob && formik.touched.dob ? 'dob-error' : ''
                  }
                  aria-required="true"
                />
              )}
            </InputMask>
            <Form.Control.Feedback id="dob-error" type='invalid' role='alert'>
              {renderFormikError(formik.errors.dob)}{' '}
              {memberType === 'proxy' && isBefore(new Date(formik.values.dob), sub(new Date(), { years: 18 })) && (
                <>
                  If an exception is needed, please <Link to='/contact'>contact us</Link>.
                </>
              )}
            </Form.Control.Feedback>
          </>
        )}
      </Form.Group>
      <Form.Group className='mt-3'>
        {appConfig.use_ssn_flag && (
          <>
            <Form.Label htmlFor='ssn'>Last 4 Digits of SSN:</Form.Label>
            <Form.Control
              id='ssn'
              type='text'
              name='ssn'
              value={formik.values.ssn}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.ssn && !!formik.touched.ssn}
              aria-invalid={!!formik.errors.ssn && !!formik.touched.ssn}
              aria-describedby={
                formik.errors.ssn && formik.touched.ssn ? 'ssn-error' : ''
              }
              aria-required="true"
            />

            <Form.Control.Feedback id="ssn-error" type='invalid' role='alert'>
              {renderFormikError(formik.errors.ssn)}
            </Form.Control.Feedback>
          </>
        )}
      </Form.Group>
      <Link to='/register/requestor' className='btn btn-light me-3'>
        &lt;&lt; Back
      </Link>
      <Button variant='primary' type='submit' disabled={isLoading}>
        {isLoading ? (
          <FhirLoader small={true} />
        ) : 'Next >>'}
      </Button>
    </Form>
  )
}

export default Member
